import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    IconButton,
    TextField,
    Button,
    Modal as MuiModal,
    Slider,
    CircularProgress
} from '@mui/material';
import { CloseOutlinedIcon, PersonOutlineOutlinedIcon } from 'icons/materialIcons';
import Cropper from 'react-easy-crop';
import getCroppedImg from 'team/shared/utils/images/teamCropImageUtils';
import { getSubteam, updateSubteam } from 'team/apis/teamSubteamApi';
import { uploadImageBlob } from 'apis/imageStorageApi';
import TeamNotificationSnackbar from 'team/shared/components/notification/TeamNotificationSnackbar';
import TeamWhiteButtonWithIcon from 'team/shared/components/button/TeamWhiteButtonWithIcon';

// Text field styles matching existing patterns
const textFieldStyles = {
    backgroundColor: '#f7f7f7',
    borderRadius: '12px',
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        '& fieldset': {
            border: 'none',
            borderRadius: '12px',
        },
        '&:hover fieldset': {
            border: 'none',
            borderRadius: '12px',
        },
        '&.Mui-focused fieldset': {
            border: 'none',
            borderRadius: '12px',
        },
        '& input': {
            borderRadius: '12px',
            padding: '10px 12px',
        },
        '& textarea': {
            borderRadius: '12px',
            padding: '10px 12px',
        },
        '& input::placeholder': {
            fontSize: '0.8rem',
            fontWeight: '300',
        },
        '& textarea::placeholder': {
            fontSize: '0.8rem',
            fontWeight: '300',
        },
    },
};

const TeamSubteamMembersGeneralContainer = ({ subteamId }) => {
    const [subteamName, setSubteamName] = useState('');
    const [description, setDescription] = useState('');
    const [logoUrl, setLogoUrl] = useState(null);
    const [logoImage, setLogoImage] = useState(null);
    const [logoCroppedImage, setLogoCroppedImage] = useState(null);
    const [logoCroppedBlob, setLogoCroppedBlob] = useState(null);
    const [logoObjectUrl, setLogoObjectUrl] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    // Store original values for cancel functionality and change detection
    const [originalSubteamName, setOriginalSubteamName] = useState('');
    const [originalDescription, setOriginalDescription] = useState('');
    const [originalLogoUrl, setOriginalLogoUrl] = useState(null);

    // Check if there are any changes
    const hasChanges = subteamName.trim() !== originalSubteamName.trim() ||
        description.trim() !== originalDescription.trim() ||
        logoCroppedBlob !== null ||
        (logoUrl !== originalLogoUrl && logoUrl !== null && originalLogoUrl !== null);
    const [notification, setNotification] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    // Image cropping states
    const [cropModalOpen, setCropModalOpen] = useState(false);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

    // Fetch subteam data
    useEffect(() => {
        const fetchSubteamData = async () => {
            if (!subteamId) return;

            setIsLoading(true);
            try {
                const response = await getSubteam(subteamId);
                if (response && response.success !== false) {
                    const subteam = response.subteam || response;
                    const name = subteam.name || '';
                    const desc = subteam.description || '';
                    const logo = subteam.logoUrl || null;
                    setSubteamName(name);
                    setDescription(desc);
                    setLogoUrl(logo);
                    // Store original values
                    setOriginalSubteamName(name);
                    setOriginalDescription(desc);
                    setOriginalLogoUrl(logo);
                }
            } catch (error) {
                console.error('Error fetching subteam data:', error);
                setNotification({
                    open: true,
                    message: 'Failed to load subteam data',
                    severity: 'error'
                });
            } finally {
                setIsLoading(false);
            }
        };

        fetchSubteamData();
    }, [subteamId]);

    // Calculate initial zoom
    const calculateInitialZoom = useCallback((mediaWidth, mediaHeight) => {
        const containerWidth = window.innerWidth * 0.9;
        const containerHeight = window.innerHeight * 0.9;
        const widthRatio = containerWidth / mediaWidth;
        const heightRatio = containerHeight / mediaHeight;
        return Math.min(widthRatio, heightRatio);
    }, []);

    // Media loaded callback
    const onMediaLoaded = useCallback((mediaSize) => {
        const initialZoom = calculateInitialZoom(mediaSize.width, mediaSize.height);
        setZoom(initialZoom);
        setCrop({ x: 0, y: 0 });
    }, [calculateInitialZoom]);

    // Handle image change
    const handleImageChange = (event) => {
        if (event.target.files && event.target.files[0]) {
            const file = URL.createObjectURL(event.target.files[0]);
            setLogoObjectUrl(file);
            setLogoImage(file);

            // Create an image object to calculate initial zoom
            const img = new Image();
            img.onload = () => {
                const initialZoom = calculateInitialZoom(img.width, img.height);
                setZoom(initialZoom);
            };
            img.src = file;

            setCropModalOpen(true);
            event.target.value = '';
        }
    };

    // Handle image removal
    const handleRemoveLogo = () => {
        setLogoCroppedImage(null);
        setLogoCroppedBlob(null);
        setLogoImage(null);
        setLogoUrl(null);

        if (logoObjectUrl) {
            URL.revokeObjectURL(logoObjectUrl);
            setLogoObjectUrl(null);
        }
    };

    // Handle notification close
    const handleNotificationClose = () => {
        setNotification({ ...notification, open: false });
    };

    // Handle crop modal close
    const handleCropClose = () => {
        setCropModalOpen(false);
        setLogoImage(null);
        if (logoObjectUrl) {
            URL.revokeObjectURL(logoObjectUrl);
            setLogoObjectUrl(null);
        }
        setZoom(1);
    };

    // Crop complete handler
    const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    // Handle save (cropping)
    const handleCropSave = async () => {
        try {
            const croppedImageBlob = await getCroppedImg(
                logoImage,
                croppedAreaPixels,
                'circle',
                'subteam-logo'
            );

            // Store the blob for later upload
            setLogoCroppedBlob(croppedImageBlob);

            // Generate a URL for the cropped image Blob for preview
            const croppedImageUrl = URL.createObjectURL(croppedImageBlob);
            setLogoCroppedImage(croppedImageUrl);

            // Close the crop modal
            setCropModalOpen(false);
        } catch (e) {
            console.error('Error cropping image:', e);
            setNotification({
                open: true,
                message: 'Failed to crop image. Please try again.',
                severity: 'error'
            });
        }
    };

    // Handle cancel - reset to original values
    const handleCancel = () => {
        setSubteamName(originalSubteamName);
        setDescription(originalDescription);
        setLogoUrl(originalLogoUrl);
        // Reset logo changes
        setLogoCroppedImage(null);
        setLogoCroppedBlob(null);
        setLogoImage(null);
        if (logoObjectUrl) {
            URL.revokeObjectURL(logoObjectUrl);
            setLogoObjectUrl(null);
        }
    };

    // Handle submit
    const handleSubmit = async () => {
        if (!subteamName.trim()) {
            setNotification({
                open: true,
                message: 'Subteam name is required',
                severity: 'error'
            });
            return;
        }

        if (!subteamId) {
            setNotification({
                open: true,
                message: 'Subteam ID is missing. Please refresh the page.',
                severity: 'error'
            });
            return;
        }

        setIsSubmitting(true);

        try {
            let finalLogoUrl = logoUrl;

            // Upload logo if a new one was cropped
            if (logoCroppedBlob) {
                try {
                    finalLogoUrl = await uploadImageBlob(
                        logoCroppedBlob,
                        null,
                        'subteam',
                        'logo',
                        subteamId
                    );
                    console.log('[TeamSubteamMembersGeneralContainer] Logo uploaded successfully:', finalLogoUrl);
                } catch (uploadError) {
                    console.error('[TeamSubteamMembersGeneralContainer] Error uploading logo:', uploadError);
                    setNotification({
                        open: true,
                        message: 'Failed to upload logo. Updating subteam without logo change.',
                        severity: 'warning'
                    });
                    // Continue with existing logo
                }
            }

            // Update subteam
            const subteamData = {
                name: subteamName.trim(),
                description: description.trim() || '',
                logoUrl: finalLogoUrl,
            };

            console.log('[TeamSubteamMembersGeneralContainer] Updating subteam:', subteamData);

            const response = await updateSubteam(subteamId, subteamData);

            if (response && response.success !== false) {
                // Update local state
                setLogoUrl(finalLogoUrl);
                if (logoCroppedBlob) {
                    // Clean up old object URLs
                    if (logoObjectUrl) {
                        URL.revokeObjectURL(logoObjectUrl);
                    }
                    if (logoCroppedImage) {
                        URL.revokeObjectURL(logoCroppedImage);
                    }
                    setLogoCroppedBlob(null);
                    setLogoCroppedImage(null);
                    setLogoObjectUrl(null);
                }

                // Update original values to reflect the saved state
                setOriginalSubteamName(subteamName.trim());
                setOriginalDescription(description.trim());
                setOriginalLogoUrl(finalLogoUrl);

                // Dispatch custom event to notify navbar to refresh
                window.dispatchEvent(new CustomEvent('subteamUpdated', {
                    detail: { subteamId }
                }));

                setIsSubmitting(false);
                setNotification({
                    open: true,
                    message: 'Subteam updated successfully!',
                    severity: 'success'
                });
            } else {
                throw new Error(response?.message || 'Failed to update subteam');
            }
        } catch (error) {
            console.error('[TeamSubteamMembersGeneralContainer] Error updating subteam:', error);
            setNotification({
                open: true,
                message: error.message || 'Failed to update subteam. Please try again.',
                severity: 'error'
            });
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    // Display logo - either cropped image or existing logo URL
    const displayLogo = logoCroppedImage || logoUrl;

    return (
        <>
            <Box sx={{
                padding: { xs: '1.5rem', sm: '2rem' },
                paddingTop: { xs: '0.5rem', sm: '1rem' },
                paddingLeft: { xs: '1.25rem', sm: '1.75rem' },
                width: '100%'
            }}>
                <Box sx={{
                    width: '100%',
                    maxWidth: { xs: '100%', sm: '700px', md: '700px' }
                }}>
                    <Typography variant="h6" sx={{ marginBottom: 3 }}>General</Typography>

                    {/* Logo Upload Section */}
                    <Box sx={{ marginBottom: 4 }}>
                        {/* Heading */}
                        <Typography
                            sx={{
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                color: '#4f4f4f',
                                marginBottom: 2
                            }}
                        >
                            Subteam logo
                        </Typography>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                            {/* Upload Circle */}
                            <Box
                                sx={{
                                    position: 'relative',
                                    width: 100,
                                    height: 100,
                                    flexShrink: 0
                                }}
                            >
                                <Box
                                    sx={{
                                        width: '100%',
                                        height: '100%',
                                        borderRadius: '50%',
                                        backgroundColor: '#f2f2f2',
                                        border: '1px solid #dbdbdb',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        textAlign: 'center',
                                        padding: 1,
                                        backgroundImage: displayLogo ? `url(${displayLogo})` : 'none',
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center',
                                    }}
                                    component="label"
                                >
                                    {!displayLogo && (
                                        <>
                                            <PersonOutlineOutlinedIcon sx={{ fontSize: '28px', color: '#828282', marginBottom: '5px' }} />
                                            <Typography
                                                variant="body2"
                                                sx={{ fontSize: '0.7rem', fontWeight: 400, color: '#212121' }}
                                            >
                                                <span style={{ color: '#29aef8' }}>Select</span> file or drag and drop here
                                            </Typography>
                                        </>
                                    )}

                                    <input
                                        id="subteam-logo-upload"
                                        type="file"
                                        accept="image/*"
                                        onChange={handleImageChange}
                                        style={{
                                            position: 'absolute',
                                            width: '100%',
                                            height: '100%',
                                            top: 0,
                                            left: 0,
                                            opacity: 0,
                                            cursor: 'pointer',
                                            pointerEvents: displayLogo ? 'none' : 'auto',
                                        }}
                                    />
                                </Box>

                                {/* Close Button */}
                                {displayLogo && (
                                    <IconButton
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleRemoveLogo();
                                        }}
                                        sx={{
                                            position: 'absolute',
                                            top: -1,
                                            right: -3,
                                            color: '#828282',
                                            backgroundColor: 'rgb(255, 255, 255)',
                                            '&:hover': { backgroundColor: 'rgb(255, 255, 255)' },
                                            width: 26,
                                            height: 26,
                                            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                                            zIndex: 2,
                                        }}
                                    >
                                        <CloseOutlinedIcon sx={{ fontSize: 12 }} />
                                    </IconButton>
                                )}
                            </Box>

                            {/* Upload Text and Help Text */}
                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                <Typography
                                    sx={{
                                        fontSize: '0.75rem',
                                        color: '#757575',
                                        lineHeight: 1.5,
                                        marginBottom: 1
                                    }}
                                >
                                    Help your teammates represent their team by adding a logo.
                                </Typography>
                                <Typography
                                    component="label"
                                    sx={{
                                        fontSize: '0.875rem',
                                        fontWeight: 400,
                                        color: '#29aef8',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            color: '#29aef8',
                                        },
                                        display: 'inline-block'
                                    }}
                                    htmlFor="subteam-logo-upload"
                                >
                                    Upload photo
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    {/* Subteam Name */}
                    <Box sx={{ marginBottom: 3 }}>
                        <Typography
                            sx={{
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                color: '#4f4f4f',
                                marginBottom: '10px',
                                display: 'flex',
                                alignItems: 'center'
                            }}
                        >
                            Subteam name
                            <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
                        </Typography>
                        <TextField
                            fullWidth
                            value={subteamName}
                            onChange={(e) => setSubteamName(e.target.value)}
                            placeholder="Enter subteam name"
                            required
                            sx={textFieldStyles}
                        />
                    </Box>

                    {/* Description */}
                    <Box sx={{ marginBottom: 3 }}>
                        <Typography
                            sx={{
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                color: '#4f4f4f',
                                marginBottom: '10px'
                            }}
                        >
                            Description
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={2}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Enter description"
                            sx={textFieldStyles}
                        />
                    </Box>

                    {/* Cancel and Update Buttons */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, marginTop: 4 }}>
                        <TeamWhiteButtonWithIcon
                            text="Cancel"
                            onClick={handleCancel}
                            disabled={isSubmitting || !hasChanges}
                            padding="10px 32px"
                            sx={{
                                minWidth: '120px',
                                '&.Mui-disabled': {
                                    backgroundColor: '#f5f5f5',
                                    color: '#c4c4c4',
                                    borderColor: '#e3e3e3'
                                }
                            }}
                        />
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={!subteamName.trim() || isSubmitting || !hasChanges}
                            sx={{
                                backgroundColor: '#000',
                                color: '#fff',
                                borderRadius: '20px',
                                textTransform: 'none',
                                fontSize: '0.7rem',
                                fontWeight: '600',
                                padding: '10px 32px',
                                minWidth: '120px',
                                '&:hover': {
                                    backgroundColor: '#333',
                                },
                                '&.Mui-disabled': {
                                    backgroundColor: '#e0e0e0',
                                    color: '#a6a6a6'
                                }
                            }}
                        >
                            {isSubmitting ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CircularProgress size={16} sx={{ color: '#fff' }} />
                                    Updating...
                                </Box>
                            ) : (
                                'Update'
                            )}
                        </Button>
                    </Box>
                </Box>
            </Box>

            {/* Crop Modal */}
            <MuiModal
                open={cropModalOpen}
                onClose={handleCropClose}
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backdropFilter: 'blur(7px)',
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                }}
            >
                <Box
                    sx={{
                        outline: 'none',
                        width: '90vw',
                        maxWidth: '900px',
                        height: '90vh',
                        borderRadius: 8,
                        maxHeight: '700px',
                    }}
                >
                    {logoImage && (
                        <>
                            {/* Container for Cropper */}
                            <Box
                                sx={{
                                    position: 'relative',
                                    width: '100%',
                                    height: 'calc(100% - 100px)',
                                    marginBottom: 2,
                                }}
                            >
                                <Cropper
                                    image={logoImage}
                                    crop={crop}
                                    zoom={zoom}
                                    aspect={1}
                                    cropShape="round"
                                    onCropChange={setCrop}
                                    onZoomChange={setZoom}
                                    onCropComplete={onCropComplete}
                                    onMediaLoaded={onMediaLoaded}
                                    objectFit="contain"
                                    centerImage={true}
                                    showGrid={false}
                                    minZoom={0.1}
                                    maxZoom={3}
                                    restrictPosition={false}
                                />
                            </Box>

                            {/* Controls for Zoom Slider and Buttons */}
                            <Box sx={{ textAlign: 'center' }}>
                                <Slider
                                    value={zoom}
                                    onChange={(e, newValue) => setZoom(newValue)}
                                    min={0.1}
                                    max={3}
                                    step={0.01}
                                    valueLabelDisplay="auto"
                                    defaultValue={1}
                                    sx={{
                                        marginBottom: 2,
                                        width: '80%',
                                        margin: '0 auto 20px auto'
                                    }}
                                />
                                <div>
                                    <Button onClick={handleCropClose} variant="contained" sx={{ marginRight: 2 }}>
                                        Cancel
                                    </Button>
                                    <Button onClick={handleCropSave} variant="contained" color="primary">
                                        Crop
                                    </Button>
                                </div>
                            </Box>
                        </>
                    )}
                </Box>
            </MuiModal>

            {/* Notification Snackbar */}
            <TeamNotificationSnackbar
                open={notification.open}
                message={notification.message}
                severity={notification.severity}
                handleClose={handleNotificationClose}
            />
        </>
    );
};

export default TeamSubteamMembersGeneralContainer;
